import { useCallback, useEffect, useRef } from 'react';

/**
 * On-device dictation via the browser SpeechRecognition (Web Speech) API.
 * Transcribes locally and fills the composer — no audio leaves the browser and
 * no server-side STT is involved. start() opens the mic; interim text streams
 * via onInterim, finalized phrases via onFinal; stop() ends the session.
 */
interface VoiceCaptureOptions {
  onInterim: (text: string) => void;
  onFinal: (text: string) => void;
  onError?: (err: unknown) => void;
}

// Minimal typings — SpeechRecognition isn't in the default DOM lib.
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: unknown) => void) | null;
  onend: (() => void) | null;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }>;
}

function getRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useVoiceCapture({
  onInterim,
  onFinal,
  onError,
}: VoiceCaptureOptions) {
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const onInterimRef = useRef(onInterim);
  const onFinalRef = useRef(onFinal);
  const onErrorRef = useRef(onError);
  onInterimRef.current = onInterim;
  onFinalRef.current = onFinal;
  onErrorRef.current = onError;

  const stop = useCallback(() => {
    const rec = recRef.current;
    recRef.current = null;
    if (rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      try {
        rec.stop();
      } catch {
        /* already stopped */
      }
    }
  }, []);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      onErrorRef.current?.(
        new Error('Speech recognition is not supported in this browser.')
      );
      return;
    }
    const rec = new Ctor();
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        const text = r[0]?.transcript ?? '';
        if (r.isFinal) {
          if (text.trim()) onFinalRef.current(text.trim());
        } else {
          interim += text;
        }
      }
      if (interim) onInterimRef.current(interim);
    };
    rec.onerror = (e) => onErrorRef.current?.(e);
    rec.onend = () => {
      recRef.current = null;
    };
    recRef.current = rec;
    try {
      rec.start();
    } catch (err) {
      onErrorRef.current?.(err);
    }
  }, []);

  useEffect(() => stop, [stop]);

  return { start, stop };
}
